import React, { memo, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ModalHeader from '../../../shared/components/ModalHeader';
import { useViewport } from '../../../shared/hooks/useViewport';
import WorkflowStages from './WorkflowStages';
import WorkflowDiagram from './WorkflowDiagram';
import WorkflowRunActions from './WorkflowRunActions';
import WorkflowStrandedRun from './WorkflowStrandedRun';
import WorkflowAbandonDialog from './WorkflowAbandonDialog';
import WorkflowStopRunDialog from './WorkflowStopRunDialog';
import { useWorkflowWrite } from '../hooks/useWorkflowWrite';
import { isRunActive } from '../runStatus';
import type { WorkflowRunDetail } from '../../../types/backend';

interface Props {
  open: boolean;
  detail: WorkflowRunDetail | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  /** Omitted while workflows is switched off: an open gate is shown, not answered. */
  onResolveGate?: (nodeId: string, approved: boolean, note?: string) => Promise<void>;
  /** Retry and abandon. Omitted, the modal offers neither, as a view of the run alone.
   *  Both name their run: the same handlers serve the sidebar row, which acts on a run that
   *  is not the open one. Retry is omitted while workflows is switched off; abandon stays
   *  (FS-UC-02 3a). */
  onRetryStep?: (runId: string, nodeId: string, stepLabel: string) => Promise<void>;
  onAbandonRun?: (runId: string, reason: string) => Promise<void>;
  /** Opens the Studio on the run's workflow draft, offered on an access failure. */
  onEditWorkflow?: (workflowId: string) => void;
  /** Why this view offers no writes, when it offers none. The absence of a handler does not say
   *  whether the switch withheld it or the surface never acts, and the two read differently to an
   *  operator, so the reason is passed rather than inferred. */
  noWritesBecause?: 'switched-off' | 'another-surface';
}

// The roomy view: every stage plus the path taken. Reached from a sidebar row, the same way
// FindingViewModal and KnowledgeViewModal are, so this adds no new navigation idiom.
const WorkflowRunModal = memo<Props>(({
  open,
  detail,
  loading,
  error,
  onClose,
  onResolveGate,
  onRetryStep,
  onAbandonRun,
  onEditWorkflow,
  noWritesBecause = 'switched-off',
}) => {
  const { isPhone } = useViewport();
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [resolving, setResolving] = useState<string | null>(null);
  const [gateError, setGateError] = useState<string | null>(null);
  const [abandoning, setAbandoning] = useState(false);
  // The stranded branch draws its own abandon, so it needs the same refusal handling the full
  // view gets from WorkflowRunActions.
  const {
    busy: strandedBusy,
    error: strandedError,
    clearError: clearStrandedError,
    write: strandedWrite,
  } = useWorkflowWrite();

  // The pinned version, or nothing when it was deleted. Named once so every reader below is
  // forced past the same question rather than reaching through `detail` and finding undefined.
  const plan = detail?.plan;

  // Every unresolved gate, not the latest one. A gate is per node and nothing limits a layer to
  // one, so two sibling steps can both be waiting at once. Showing only one leaves the other with
  // no affordance anywhere and the run parked on an answer the operator cannot give.
  const openGates = useMemo(
    () => (detail?.run.gates ?? []).filter((g) => !g.resolution),
    [detail],
  );

  // Memoised with openGates: each gate card names its step, and the plan is fixed within a run.
  const labels = useMemo(
    () => new Map((detail?.plan?.nodes ?? []).map((n) => [n.id, n.label])),
    [detail],
  );

  // Memoised with labels: an access failure names the tools its step allows.
  const grants = useMemo(
    () => new Map((detail?.plan?.nodes ?? []).map((n) => [n.id, n.allowed_tools ?? []])),
    [detail],
  );

  // Keyed on the gate, not on a boolean: the same reason the band's dialog is. Ending a run is
  // irreversible, and the note field beside the button is a pause, not a confirmation.
  const [stoppingGate, setStoppingGate] = useState<string | null>(null);

  const handleResolve = async (nodeId: string, approved: boolean) => {
    if (!onResolveGate) return;
    setResolving(nodeId);
    setGateError(null);
    try {
      await onResolveGate(nodeId, approved, notes[nodeId]?.trim() || undefined);
      setNotes((prev) => ({ ...prev, [nodeId]: '' }));
    } catch (e) {
      // This is the one action the feature insists a person takes, so a silent failure is the
      // worst case: the operator walks away believing they approved a step that never moved.
      setGateError(e instanceof Error ? e.message : 'That answer did not reach the run');
    } finally {
      setResolving(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isPhone}>
      <ModalHeader
        icon={AccountTreeIcon}
        title={detail ? detail.run.workflow_ref.title : 'Workflow run'}
        onClose={onClose}
      />
      <DialogContent dividers>
        {/* The error is read before the spinner: a failed fetch leaves detail null with loading
            false, and the spinner would then turn forever on a run that will never arrive. */}
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : loading || !detail ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : !plan ? (
          /* The definition this run pinned was deleted, so there is no procedure to draw and
             nothing to retry into: every write tool is refused while the plan is gone, so a
             retry would show the run as running and it could never move. Its record, and the
             one act that still means something. */
          <Stack spacing={2}>
            <WorkflowStrandedRun run={detail.run} canAbandon={Boolean(onAbandonRun)} />
            {strandedError && (
              <Alert severity="error" onClose={clearStrandedError}>
                {strandedError}
              </Alert>
            )}
            {/* Only while there is something to stop. A run that already completed or was
                abandoned is a record, and offering to abandon it again asks the backend a
                question it will refuse. */}
            {onAbandonRun && isRunActive(detail.run.status ?? '') && (
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  size="small"
                  color="error"
                  disabled={strandedBusy}
                  onClick={() => setAbandoning(true)}
                >
                  Abandon run
                </Button>
              </Stack>
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            {/* The whole procedure first, with the run on it: an operator opening a run asks
                where it stands and where it can still go before they read any one step. */}
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1.5,
                p: 1.5,
                bgcolor: 'background.default',
              }}
            >
              <WorkflowDiagram nodes={plan.nodes} run={detail.run} />
            </Box>

            {gateError && (
              <Alert severity="error" onClose={() => setGateError(null)}>
                {gateError}
              </Alert>
            )}
            {openGates.map((gate) => (
              <Alert
                key={gate.node_id}
                severity="warning"
                action={onResolveGate && (
                  <Stack direction="row" spacing={1}>
                    {/* Declining a gate abandons the whole run: WorkflowRun.resolve_gate sets
                        status to ABANDONED on a REJECTED resolution. The label states that
                        blast radius rather than reading as a decline of one step. */}
                    <Button
                      size="small"
                      color="inherit"
                      disabled={resolving !== null}
                      onClick={() => setStoppingGate(gate.node_id)}
                    >
                      Stop run
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      disabled={resolving !== null}
                      onClick={() => handleResolve(gate.node_id, true)}
                    >
                      Approve
                    </Button>
                  </Stack>
                )}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {labels.get(gate.node_id) ?? gate.node_id} is waiting on you
                </Typography>
                <Typography variant="caption">{gate.reason}</Typography>
                {onResolveGate ? (
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Add a note (optional)"
                    value={notes[gate.node_id] ?? ''}
                    onChange={(e) =>
                      setNotes((prev) => ({ ...prev, [gate.node_id]: e.target.value }))
                    }
                    sx={{ mt: 1 }}
                  />
                ) : (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    {noWritesBecause === 'switched-off'
                      ? 'Workflows are switched off, so this cannot be answered now.'
                      : 'Answered from the investigation that holds this run.'}
                  </Typography>
                )}
              </Alert>
            ))}

            {openGates.some((g) => g.node_id === stoppingGate) && (
              <WorkflowStopRunDialog
                open
                stepLabel={labels.get(stoppingGate as string) ?? (stoppingGate as string)}
                onCancel={() => setStoppingGate(null)}
                onConfirm={() => {
                  const nodeId = stoppingGate as string;
                  setStoppingGate(null);
                  void handleResolve(nodeId, false);
                }}
              />
            )}

            {/* Not gated on a handler. Each control is gated on its own, and what this draws
                besides them is why the run is stuck, which a view that cannot act still owes
                the operator who opened it. */}
            <WorkflowRunActions
              run={detail.run}
              labels={labels}
              grants={grants}
              onRetryStep={
                onRetryStep &&
                ((nodeId) => onRetryStep(detail.run.run_id, nodeId, labels.get(nodeId) ?? nodeId))
              }
              onAbandonRun={
                onAbandonRun && ((reason) => onAbandonRun(detail.run.run_id, reason))
              }
              onEditWorkflow={onEditWorkflow}
            />

            <WorkflowStages nodes={plan.nodes} run={detail.run} />
          </Stack>
        )}
      </DialogContent>

      {detail && onAbandonRun && (
        <WorkflowAbandonDialog
          open={abandoning}
          runTitle={detail.run.workflow_ref.title}
          onCancel={() => setAbandoning(false)}
          onConfirm={(reason) => {
            setAbandoning(false);
            void strandedWrite(() => onAbandonRun(detail.run.run_id, reason));
          }}
        />
      )}
    </Dialog>
  );
});

WorkflowRunModal.displayName = 'WorkflowRunModal';

export default WorkflowRunModal;
